import ImagemProduto from '@/components/feature/ImagemProduto';
import { formatarCentavos } from '@/pages/loja/utils';
import type { PontoCaixa, ProdutoCaixa } from '../types';

interface Props {
  pontos: PontoCaixa[];
  quantidades: Map<number, number>;
  aoAdicionar: (p: ProdutoCaixa) => void;
  aoRemover: (p: ProdutoCaixa) => void;
  aoEditar: (p: ProdutoCaixa) => void;
}

// Uma cor de barrinha por barraca, na ordem em que elas aparecem.
const CORES_BARRA = [
  'bg-sky-500',
  'bg-orange-500',
  'bg-violet-500',
  'bg-emerald-500',
  'bg-rose-500',
  'bg-amber-500',
];

// Grade de produtos do caixa, agrupada por barraca. Tocar no produto = +1;
// o "−" tira um; tocar no número abre o teclado para digitar a quantidade.
export default function GradeProdutos({
  pontos,
  quantidades,
  aoAdicionar,
  aoRemover,
  aoEditar,
}: Props) {
  return (
    <div className="space-y-5">
      {pontos.map((ponto, idx) => (
        <section key={ponto.ponto_id}>
          <div className="flex items-center gap-2">
            <span
              className={`h-5 w-1.5 rounded-full ${CORES_BARRA[idx % CORES_BARRA.length]}`}
            />
            <h2 className="text-base font-bold text-foreground-950">{ponto.nome}</h2>
          </div>

          {ponto.produtos.length === 0 ? (
            <p className="mt-2 text-sm text-secondary-500">Nenhum produto nesta barraca.</p>
          ) : (
            <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {ponto.produtos.map((p) => (
                <CartaoProduto
                  key={p.produto_id}
                  produto={p}
                  quantidade={quantidades.get(p.produto_id) ?? 0}
                  aoAdicionar={aoAdicionar}
                  aoRemover={aoRemover}
                  aoEditar={aoEditar}
                />
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

function CartaoProduto({
  produto,
  quantidade,
  aoAdicionar,
  aoRemover,
  aoEditar,
}: {
  produto: ProdutoCaixa;
  quantidade: number;
  aoAdicionar: (p: ProdutoCaixa) => void;
  aoRemover: (p: ProdutoCaixa) => void;
  aoEditar: (p: ProdutoCaixa) => void;
}) {
  const esgotado = produto.esgotado;
  const alerta = !esgotado && (produto.semaforo === 'amarelo' || produto.semaforo === 'vermelho');

  return (
    <div
      className={`relative flex min-h-[116px] select-none flex-col items-center justify-center rounded-2xl border-2 bg-white text-center transition-colors ${
        esgotado
          ? 'border-transparent opacity-50'
          : alerta
            ? 'border-warning-400'
            : quantidade > 0
              ? 'border-primary-500'
              : 'border-secondary-200'
      }`}
    >
      {/* Toca em qualquer lugar do cartão = +1 */}
      <button
        type="button"
        disabled={esgotado}
        onClick={() => aoAdicionar(produto)}
        aria-label={`Adicionar ${produto.nome}`}
        className={`absolute inset-0 rounded-2xl ${esgotado ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      />

      <div className="pointer-events-none relative flex w-full flex-col items-center gap-1 p-3">
        <ImagemProduto
          imagemUrl={produto.imagem_url}
          imagemSlug={produto.imagem_slug}
          emoji={produto.emoji}
          nome={produto.nome}
          tamanho="miniatura"
          className="h-11 w-11"
        />
        <span className="line-clamp-2 text-[17px] font-extrabold leading-tight text-foreground-950">
          {produto.nome}
        </span>
        <span className="text-lg font-extrabold text-primary-700">
          {formatarCentavos(produto.preco_centavos)}
        </span>
      </div>

      {quantidade > 0 && !esgotado && (
        <>
          <button
            type="button"
            onClick={() => aoEditar(produto)}
            aria-label={`Mudar a quantidade de ${produto.nome}`}
            className="absolute right-1.5 top-1.5 z-10 flex h-10 min-w-10 cursor-pointer items-center justify-center rounded-full bg-primary-500 px-2 text-lg font-bold text-white"
          >
            {quantidade}
          </button>
          <button
            type="button"
            onClick={() => aoRemover(produto)}
            aria-label={`Tirar um ${produto.nome}`}
            className="absolute bottom-1.5 right-1.5 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-secondary-200 text-xl text-foreground-800"
          >
            <i className="ri-subtract-line" />
          </button>
        </>
      )}

      {esgotado && (
        <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-sm font-bold text-foreground-600">
          Esgotou
        </span>
      )}
    </div>
  );
}